import { sortBy } from 'lodash';
import { formatDateDefault } from 'services/DateFormatters';
import { formatBalance } from 'services/NumberFormatters';
import { isDateBefore, isDateTimeBetween } from 'services/DateService';
import { getProjectCacheExpiry } from 'services/environment';
import { selectorByIdBuilder } from './selectors';
import { uniqueSymmetricDifference } from './arrayService';
import { sortByName } from './sorting';
import { has } from 'lodash';

/**
 * calculate the new weight based on the project priorities
 * TODO - move calc to the server - done on saveProject and deleteProjectPriorities
 * @param {*} project 
 */
export const calculateWeight = (project) => {
    let updatedProject;
    if (project) {
        // calculate the new weight here
        updatedProject = { ...project, weight: 50 };
    }
    return updatedProject;
};

/**
 * sort project by weight - lowest first - call Array.reverse() to reverse the order
 * @param {*} projects 
 */
export const sortProjectsByWeight = (projects) => {
    let sortedData = [];
    if (projects) {
        sortedData = sortBy(projects, [function (o) {
            return (o.weight);
        }]);
    }
    return sortedData;
};

export const sortProjectPrioritiesByValue = (priorities) => {
    let sortedData = [];
    if (priorities) {
        sortedData = sortBy(priorities, [function (o) {
            return (o.value);
        }]);
    }
    return sortedData;
};

export const getImportProjectList = (importedProjects, allProjects) => {
    if (Array.isArray(importedProjects) && Array.isArray(allProjects)) {
        return importedProjects.filter((imported) => !allProjects.find((project) => imported?.id === project?.jiraProjectAttributes?.id));
    }
    return [];
};

/**
 * Active if the end date is before the current date
 * @param {*} projectList 
 */
export const getActiveProjects = (projectList) => {
    const currentDateFormatted = formatDateDefault(new Date());
    const activeProjects = projectList?.filter((project) => {
        const endDateFormatted = formatDateDefault(project.endDate);
        const startDateFormatted = formatDateDefault(project?.startDate);
        const datesEqual = endDateFormatted === startDateFormatted;
        const dateBefore = isDateBefore(endDateFormatted, currentDateFormatted);
        return dateBefore || datesEqual;
    });
    return activeProjects;
};

export const getProjectDisplayData = (project) => {
    let displayData = {};
    if (project) {
        displayData = {
            id: project?.id,
            summary: project?.name ? project?.name : 'No name specified',
            description: project?.description ? project?.description : 'No description specified',
            weight: project?.weight,
            startDate: formatDateDefault(project?.startDate),
            endDate: project?.endDate ? formatDateDefault(project?.endDate) : 'This project has no end date specified',
            budget: project?.budget ? formatBalance(project?.budget): formatBalance(0),
            businessValue: project?.businessValue ? project?.businessValue : 'Not specifed'
        };
    }
    return displayData;
};

/**
 * Check if date is between now and an expiry date that is set in the env prop PROJECT_CACHE_EXPIRY
 * @param {*} testDateTime 
 */
export const projectCacheExpired = async (testDateTime) => {
    if (testDateTime) {
        var now = new Date();
        const expiryDate = new Date().setMinutes(now.getMinutes() - getProjectCacheExpiry());
        return !isDateTimeBetween(now, expiryDate, testDateTime);
    }
    return true;
};

/**
 * Check each project cache entry if the date is between now and an expiry date that is set in the env prop PROJECT_CACHE_EXPIRY
 * @param {Array} cache 
 */
export const sanitizeProjectCache = async (cache) => {
    if (Array.isArray(cache)) {
        return cache.filter((cacheEntry) => !projectCacheExpired(cacheEntry?.lastUpdated));
    }
    return [];
};

export const mapIssuesInwardDependenciesToDisplayData = (allProjects, project) => {
    return mapIssuesDependenciesToDisplayData(allProjects, project, true);
};

export const mapIssuesOutwardDependenciesToDisplayData = (allProjects, project) => {
    return mapIssuesDependenciesToDisplayData(allProjects, project, false);
};


export const mapIssuesDependenciesToDisplayData = (allProjects, project, inward) => {
    let projectDependencyData;
    if (Array.isArray(project?.projectlinks) && project?.projectlinks?.length > 0) {
        projectDependencyData = new Array();
        project?.projectlinks?.map((link) => {
            let linkedProjectId = 0;
            if (inward) {
                if (link?.inwardProject !== undefined) {
                    linkedProjectId = link?.inwardProject?.id;
                }
            } else {
                if (link?.outwardProject !== undefined) {
                    linkedProjectId = link?.outwardProject?.id;
                }
            }
            const foundProject = allProjects?.find((element) => element?.id === linkedProjectId);
            if (foundProject) {
                projectDependencyData.push(getProjectDisplayData(foundProject));
            }
        });
    }
    return projectDependencyData;
};

export const mapProjectInwardDependenciesToSelectionData = (allProjects, project) => {
    return mapProjectDependenciesToSelectionData(allProjects, project, true);
};

export const mapProjectOutwardDependenciesToSelectionData = (allProjects, project) => {
    return mapProjectDependenciesToSelectionData(allProjects, project, false);
};

/**
 * turn project dependencies into a selectable list for use in the project select dependency screens
 * UPDATE - projects will not be linked in this way - code may be redundant - TBC
 * @param {*} allProjects 
 * @param {*} project 
 * @param {*} inward 
 */
export const mapProjectDependenciesToSelectionData = (allProjects, project, inward) => {
    let projectSelectionList;
    let result;
    if (Array.isArray(project?.issuelinks) && project?.issuelinks?.length > 0) {
        const projectLinkedList = new Array();
        project?.issuelinks?.map((link) => {
            let linkedId;
            if (inward) {
                if (has(link, 'inwardProject')) {
                    linkedId = link?.inwardProject?.id;
                }
            } else {
                if (has(link, 'outwardProject')) {
                    linkedId = link?.outwardProject?.id;
                }
            }
            if (linkedId) {
                const foundProject = allProjects?.find((element) => element?.id === linkedId);
                projectLinkedList.push(foundProject);
            }
            projectSelectionList = selectorByIdBuilder(projectLinkedList, 'id', 'name', true);
        });
        // need to remove the current project from the allProjects list
        const selectableProjects = allProjects.filter((entry) => entry?.id !== project?.id);
        const nonLinkedProjects = uniqueSymmetricDifference(selectableProjects, projectSelectionList);
        // sort the projects for display
        const nonLinkedprojectsSorted = sortByName(nonLinkedProjects);
        // now convert for selection
        const mappedNoLinkedProjects = selectorByIdBuilder(nonLinkedprojectsSorted, 'id', 'name', false);
        result = projectSelectionList.concat(mappedNoLinkedProjects);
    }
    return result;
};






// WEBPACK FOOTER //
// ./services/projectService.js